import type { Permission } from '../types';
import { COMMON_RESOURCES, COMMON_ACTIONS, PERMISSION_SCOPES } from '../utils/rbacUtils';

/**
 * RBAC Policy Definitions
 * Centralized policy definitions that can be easily updated and audited
 */

// Base permission templates
export const PERMISSION_TEMPLATES = {
  // Resource view permissions
  VIEW_GLOBAL: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.VIEW,
    scope: PERMISSION_SCOPES.GLOBAL
  }),
  
  VIEW_SELF: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.VIEW,
    scope: PERMISSION_SCOPES.SELF
  }),

  VIEW_POD: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.VIEW,
    scope: PERMISSION_SCOPES.POD
  }),

  // Resource management permissions
  MANAGE_GLOBAL: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.MANAGE,
    scope: PERMISSION_SCOPES.GLOBAL
  }),

  EDIT_GLOBAL: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.EDIT,
    scope: PERMISSION_SCOPES.GLOBAL
  }),

  CREATE_GLOBAL: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.CREATE,
    scope: PERMISSION_SCOPES.GLOBAL
  }),

  DELETE_GLOBAL: (resource: string): Permission => ({
    resource,
    action: COMMON_ACTIONS.DELETE,
    scope: PERMISSION_SCOPES.GLOBAL
  }),
} as const;

// Base role permissions (without inheritance)
const TEAM_MEMBER_PERMISSIONS = [
  // Dashboard access - all users should be able to access the dashboard
  {
    resource: 'dashboard',
    action: COMMON_ACTIONS.VIEW,
    scope: PERMISSION_SCOPES.GLOBAL
  },
  PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PODS),
  PERMISSION_TEMPLATES.VIEW_SELF(COMMON_RESOURCES.PROJECTS),
  PERMISSION_TEMPLATES.VIEW_SELF(COMMON_RESOURCES.USERS),
  {
    resource: 'time_entries',
    action: COMMON_ACTIONS.CREATE,
    scope: PERMISSION_SCOPES.SELF
  },
  {
    resource: 'time_entries',
    action: COMMON_ACTIONS.EDIT,
    scope: PERMISSION_SCOPES.SELF
  },
  {
    resource: 'time_entries',
    action: COMMON_ACTIONS.VIEW,
    scope: PERMISSION_SCOPES.SELF
  }
];

const ACCOUNT_MANAGER_PERMISSIONS = [
  // Client management
  PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.CLIENTS),
  PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.CLIENTS),
  PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.CLIENTS),
  // Retainer management
  PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.RETAINERS),
  PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.RETAINERS),
  PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.RETAINERS),
  // Project viewing
  {
    resource: COMMON_RESOURCES.PROJECTS,
    action: COMMON_ACTIONS.VIEW,
    scope: PERMISSION_SCOPES.CLIENT
  }
];

// Role-based permission policies (with proper inheritance)
export const ROLE_POLICIES = {
  team_member: TEAM_MEMBER_PERMISSIONS,

  account_manager: [
    ...TEAM_MEMBER_PERMISSIONS,
    ...ACCOUNT_MANAGER_PERMISSIONS
  ],

  finance: [
    // Dashboard access
    {
      resource: 'dashboard',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    // Basic viewing permissions
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.CLIENTS),
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.RETAINERS),
    // Financial reporting
    {
      resource: COMMON_RESOURCES.REPORTS,
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    {
      resource: COMMON_RESOURCES.REPORTS,
      action: COMMON_ACTIONS.CREATE,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    {
      resource: COMMON_RESOURCES.REPORTS,
      action: COMMON_ACTIONS.EXPORT,
      scope: PERMISSION_SCOPES.GLOBAL
    }
  ],

  project_manager: [
    ...TEAM_MEMBER_PERMISSIONS,
    // Analytics access
    {
      resource: 'analytics',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    {
      resource: 'analytics',
      action: COMMON_ACTIONS.READ,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    // Reporting access
    {
      resource: 'reporting',
      action: COMMON_ACTIONS.READ,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    {
      resource: COMMON_RESOURCES.REPORTS,
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.PROJECT
    },
    {
      resource: COMMON_RESOURCES.REPORTS,
      action: COMMON_ACTIONS.EXPORT,
      scope: PERMISSION_SCOPES.PROJECT
    },
    // Project management
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PROJECTS),
    {
      resource: COMMON_RESOURCES.PROJECTS,
      action: COMMON_ACTIONS.EDIT,
      scope: PERMISSION_SCOPES.PROJECT
    },
    {
      resource: COMMON_RESOURCES.PROJECTS,
      action: COMMON_ACTIONS.ASSIGN,
      scope: PERMISSION_SCOPES.PROJECT
    },
    // Team viewing
    {
      resource: 'pod_assignments',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.PROJECT
    }
  ],

  pod_lead: [
    ...TEAM_MEMBER_PERMISSIONS,
    // Project management (from project_manager)
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PROJECTS),
    {
      resource: COMMON_RESOURCES.PROJECTS,
      action: COMMON_ACTIONS.EDIT,
      scope: PERMISSION_SCOPES.PROJECT
    },
    {
      resource: COMMON_RESOURCES.PROJECTS,
      action: COMMON_ACTIONS.ASSIGN,
      scope: PERMISSION_SCOPES.PROJECT
    },
    {
      resource: 'pod_assignments',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.PROJECT
    },
    // Pod management
    {
      resource: COMMON_RESOURCES.PODS,
      action: COMMON_ACTIONS.EDIT,
      scope: PERMISSION_SCOPES.POD
    },
    {
      resource: COMMON_RESOURCES.PODS,
      action: COMMON_ACTIONS.MANAGE,
      scope: PERMISSION_SCOPES.POD
    },
    // Team management
    {
      resource: 'pod_assignments',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.POD
    },
    {
      resource: 'pod_assignments',
      action: COMMON_ACTIONS.ASSIGN,
      scope: PERMISSION_SCOPES.POD
    }
  ],

  operations: [
    // Global viewing permissions
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PODS),
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.CLIENTS),
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.RETAINERS),
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.USERS),
    // Global management permissions
    PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PODS),
    PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.PODS),
    PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.PROJECTS),
    // User management
    {
      resource: COMMON_RESOURCES.USERS,
      action: COMMON_ACTIONS.ASSIGN,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    // Reporting
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.REPORTS),
    {
      resource: COMMON_RESOURCES.REPORTS,
      action: COMMON_ACTIONS.CREATE,
      scope: PERMISSION_SCOPES.GLOBAL
    }
  ],

  admin: [] // Admin gets all permissions via special handling
} as const;

// Feature-based permission groups
export const FEATURE_POLICIES = {
  DASHBOARD_ACCESS: [
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PODS),
    {
      resource: 'dashboard',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.GLOBAL
    }
  ],

  PROJECT_MANAGEMENT: [
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PROJECTS),
    PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.PROJECTS)
  ],

  CLIENT_MANAGEMENT: [
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.CLIENTS),
    PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.CLIENTS),
    PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.CLIENTS)
  ],

  ANALYTICS_ACCESS: [
    {
      resource: 'analytics',
      action: COMMON_ACTIONS.VIEW,
      scope: PERMISSION_SCOPES.GLOBAL
    },
    PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.REPORTS)
  ],

  ADMIN_PANEL: [
    PERMISSION_TEMPLATES.MANAGE_GLOBAL(COMMON_RESOURCES.SYSTEM),
    PERMISSION_TEMPLATES.MANAGE_GLOBAL(COMMON_RESOURCES.USERS),
    {
      resource: COMMON_RESOURCES.SYSTEM,
      action: 'configure',
      scope: PERMISSION_SCOPES.GLOBAL
    }
  ]
} as const;

// UI-specific permission policies
export const UI_POLICIES = {
  NAVIGATION: {
    DASHBOARD: FEATURE_POLICIES.DASHBOARD_ACCESS,
    PODS: [PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.PODS)],
    PROJECTS: [PERMISSION_TEMPLATES.VIEW_SELF(COMMON_RESOURCES.PROJECTS)],
    CLIENTS: [PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.CLIENTS)],
    RETAINERS: [PERMISSION_TEMPLATES.VIEW_GLOBAL(COMMON_RESOURCES.RETAINERS)],
    ANALYTICS: FEATURE_POLICIES.ANALYTICS_ACCESS,
    SETTINGS: [PERMISSION_TEMPLATES.VIEW_SELF(COMMON_RESOURCES.USERS)]
  },

  BUTTONS: {
    CREATE_PROJECT: [PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PROJECTS)],
    CREATE_CLIENT: [PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.CLIENTS)],
    CREATE_POD: [PERMISSION_TEMPLATES.CREATE_GLOBAL(COMMON_RESOURCES.PODS)],
    EDIT_USER: [PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.USERS)],
    DELETE_PROJECT: [PERMISSION_TEMPLATES.DELETE_GLOBAL(COMMON_RESOURCES.PROJECTS)]
  },

  FORMS: {
    PROJECT_FORM: FEATURE_POLICIES.PROJECT_MANAGEMENT,
    CLIENT_FORM: FEATURE_POLICIES.CLIENT_MANAGEMENT,
    USER_FORM: [PERMISSION_TEMPLATES.EDIT_GLOBAL(COMMON_RESOURCES.USERS)]
  }
} as const;

/**
 * Policy evaluation functions
 */
export class PolicyEngine {
  /**
   * Get permissions for a specific role
   */
  static getPermissionsForRole(role: string): Permission[] {
    return ROLE_POLICIES[role as keyof typeof ROLE_POLICIES] || [];
  }

  /**
   * Get permissions for a specific feature
   */
  static getPermissionsForFeature(feature: keyof typeof FEATURE_POLICIES): Permission[] {
    return FEATURE_POLICIES[feature] || [];
  }

  /**
   * Get permissions for a specific UI element
   */
  static getPermissionsForUIElement(
    category: keyof typeof UI_POLICIES,
    element: string
  ): Permission[] {
    const categoryPolicies = UI_POLICIES[category] as Record<string, Permission[]>;
    return categoryPolicies[element] || [];
  }

  /**
   * Check if a role has access to a feature
   */
  static roleHasFeatureAccess(role: string, feature: keyof typeof FEATURE_POLICIES): boolean {
    const rolePermissions = this.getPermissionsForRole(role);
    const featurePermissions = this.getPermissionsForFeature(feature);
    
    return featurePermissions.every(requiredPerm =>
      rolePermissions.some(rolePerm =>
        rolePerm.resource === requiredPerm.resource &&
        rolePerm.action === requiredPerm.action &&
        (rolePerm.scope === requiredPerm.scope || rolePerm.scope === 'global')
      )
    );
  }

  /**
   * Get all features accessible by a role
   */
  static getAccessibleFeatures(role: string): string[] {
    const features = Object.keys(FEATURE_POLICIES) as (keyof typeof FEATURE_POLICIES)[];
    return features.filter(feature => this.roleHasFeatureAccess(role, feature));
  }
}

export default PolicyEngine;
